/* Bento header controller: panels, sticky bar and the mobile drawer.

   Panels: `{% content_for 'blocks' %}` renders every panel once, outside the
   nav, so the nav item and its panel are joined here: a `[data-mega-target]`
   item opens the `[data-mega-panel]` block with the same handle. Desktop hover
   and focus both open a panel; the dot button is the keyboard entry point,
   because the panel markup sits after the whole nav container and Tab alone
   would have to walk past every other item — each of which closes the panel
   again.

   Sticky and the drawer used to come from the vendor `header.js` +
   `mega-menu.js`; they live here now. The class contract is unchanged
   (`stuck`, `header-sticky-always`, `scroll-up`, `scroll-down`, the body
   classes and the drawer's `open` / `header-drawer-open` / `m-submenu-open*` /
   `open-submenu` / `--menu-drawer-height`), so the "Base" block of
   `assets/header-bento.css` (ported from the vendor `header.css`),
   `assets/main.css` and `docs/_archiwum/benchmarks/measure-header.js` keep working. The
   sticky positioning itself comes from that "Base" block. */
(function () {
  function initPanels(header, drawer, disposers) {
    var panels = header.querySelector('[data-mega-panels]');
    if (!panels) return;

    var items = Array.prototype.slice.call(header.querySelectorAll('[data-mega-target]'));
    var submenus = Array.prototype.slice.call(header.querySelectorAll('.m-megamenu-mobile'));
    var overlay = header.querySelector('.m-header__overlay');

    var closeTimer = null;
    var activeItem = null;
    var restoringFocus = false;

    /* A panel's home is whatever it was rendered into — the `.shopify-block`
       wrapper inside the padded `panels-content`, not `panels-inner`. Sending it
       to `panels-inner` instead drops the container padding (content flush to the
       card edge) and strips the wrapper the theme editor selects the block by. */
    var homes = new Map();
    Array.prototype.forEach.call(header.querySelectorAll('[data-mega-panel]'), function (panel) {
      homes.set(panel, panel.parentNode);
    });

    function sendHome(panel) {
      var home = homes.get(panel);
      if (home && panel.parentNode !== home) home.appendChild(panel);
    }

    /* The panel's height budget starts where the bar ends on screen — that
       already includes the topbar when there is one and follows a sticky bar. */
    var bar = header.querySelector('.m-header__desktop');
    function measurePanelTop() {
      panelTopFrame = null;
      if (bar) header.style.setProperty('--m-bento-panel-top', Math.round(bar.getBoundingClientRect().bottom) + 'px');
    }

    /* Same rAF coalescing as `scheduleBarBottom` below: `resize` and `scroll`
       can each fire many times a frame, and every call was a
       `getBoundingClientRect` read plus a style write. Gated on `activeItem`
       same as before — a closed panel has nothing reading the variable. */
    var panelTopFrame = null;
    function schedulePanelTop() {
      if (!activeItem) return;
      if (!panelTopFrame) panelTopFrame = window.requestAnimationFrame(measurePanelTop);
    }
    function onResizePanelTop() {
      schedulePanelTop();
    }
    function onScrollPanelTop() {
      schedulePanelTop();
    }
    window.addEventListener('resize', onResizePanelTop);
    window.addEventListener('scroll', onScrollPanelTop, { passive: true });
    disposers.push(function () {
      window.removeEventListener('resize', onResizePanelTop);
      window.removeEventListener('scroll', onScrollPanelTop);
      if (panelTopFrame) {
        window.cancelAnimationFrame(panelTopFrame);
        panelTopFrame = null;
      }
    });

    /* The bar's bottom edge, published as `--m-bento-bar-bottom` on the root
       for cards that hang under the header (the 404 page, the search card).
       Measured on load and on resize. The menu and cart drawers no longer
       read it: since 2026-10-14 they cover the whole screen, bar included. */
    var mobileBar = header.querySelector('.m-header__mobile');
    var barFrame = null;
    function measureBarBottom() {
      barFrame = null;
      var visible = mobileBar && mobileBar.offsetParent !== null ? mobileBar : bar;
      if (!visible) return;
      document.documentElement.style.setProperty(
        '--m-bento-bar-bottom',
        Math.round(visible.getBoundingClientRect().bottom) + 'px'
      );
    }
    function scheduleBarBottom() {
      if (!barFrame) barFrame = window.requestAnimationFrame(measureBarBottom);
    }
    measureBarBottom();
    window.addEventListener('resize', scheduleBarBottom);
    disposers.push(function () {
      window.removeEventListener('resize', scheduleBarBottom);
      if (barFrame) {
        window.cancelAnimationFrame(barFrame);
        barFrame = null;
      }
    });

    var cartDrawer = document.querySelector('m-cart-drawer');

    /* Switching between the cart and the menu hands the scroll lock from one
       to the other, and `cart-drawer-bento.js` releases it late: it removes
       `prevent-scroll` 300ms after close starts — by then the menu drawer may
       be open and have already set it. The page would scroll under an open
       drawer (and the sticky bar slide away with the menu). While either one
       is still open, put the lock back. */
    var menuDrawer = header.querySelector('#m-menu-drawer');
    var root = document.documentElement;
    var scrollLockObserver = new MutationObserver(function () {
      if (root.classList.contains('prevent-scroll')) return;
      var menuOpen = menuDrawer && menuDrawer.classList.contains('open');
      var cartOpen = cartDrawer && cartDrawer.classList.contains('m-cart-drawer--active');
      if (menuOpen || cartOpen) root.classList.add('prevent-scroll');
    });
    scrollLockObserver.observe(root, { attributes: true, attributeFilter: ['class'] });
    // This one observes `root`, not `header`, so a section reload never
    // detaches it on its own — left running, it re-adds `prevent-scroll` on
    // every future class change to `<html>` from a `menuDrawer` that no
    // longer exists on screen. Disposed alongside the rest below.
    disposers.push(function () {
      scrollLockObserver.disconnect();
    });

    /* The menu button is a real button (header-bento.liquid); the drawer
       handler toggles `.active` on it, so `aria-expanded` follows that class. */
    var burger = header.querySelector('.m-hamburger-box');
    if (burger) {
      var burgerExpandedObserver = new MutationObserver(function () {
        burger.setAttribute('aria-expanded', burger.classList.contains('active') ? 'true' : 'false');
      });
      burgerExpandedObserver.observe(burger, { attributes: true, attributeFilter: ['class'] });
      disposers.push(function () {
        burgerExpandedObserver.disconnect();
      });
    }

    function panelFor(target) {
      return header.querySelector('[data-mega-panel="' + target + '"]');
    }

    function toggleFor(item) {
      return item.querySelector('[data-mega-toggle]');
    }

    function setExpanded(item, expanded) {
      var toggle = toggleFor(item);
      if (toggle) toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    }

    function activate(item, moveFocus) {
      var panel = panelFor(item.dataset.megaTarget);
      if (!panel) return;

      window.clearTimeout(closeTimer);
      // A plain dropdown and a bento panel are never open at the same time.
      (dropdownItems || []).forEach(function (other) {
        setDropdown(other, false);
      });

      if (activeItem && activeItem !== item) {
        activeItem.classList.remove('is-mega-open');
        setExpanded(activeItem, false);
      }

      // A panel may be living in a drawer slot; bring it home before showing it.
      sendHome(panel);
      measurePanelTop();

      Array.prototype.forEach.call(panels.querySelectorAll('.m-bento-panel.is-active'), function (node) {
        if (node !== panel) node.classList.remove('is-active');
      });

      panel.classList.add('is-active');
      panels.classList.add('is-open');
      header.classList.add('m-bento-active');
      /* Not the vendor's `m-menu__item--active`: nothing sets it any more, and
         the pill state is this class. */
      item.classList.add('is-mega-open');
      setExpanded(item, true);
      activeItem = item;

      if (moveFocus) {
        var focusable = panel.querySelector('a[href], button:not([disabled])');
        if (focusable) focusable.focus();
      }
    }

    /* The panel keeps `is-active` after closing: the card slides away with its
       content (header-bento.css), and dropping the content at once would snap
       the card down to its padding mid-slide. The closed container is hidden,
       and the next `activate` swaps the active panel before the card shows. */
    function deactivate(returnFocus) {
      // One pending close at a time: an older timer left running would close
      // a panel the pointer has since come back to.
      window.clearTimeout(closeTimer);
      closeTimer = window.setTimeout(function () {
        panels.classList.remove('is-open');
        header.classList.remove('m-bento-active');

        if (activeItem) {
          var closing = activeItem;
          closing.classList.remove('is-mega-open');
          setExpanded(closing, false);
          activeItem = null;
          /* Focus goes back to the toggle, which sits inside the item: its
             `focusin` would open the panel again at once (Escape looked
             dead, and two items ended up marked open). */
          if (returnFocus) {
            var toggle = toggleFor(closing);
            if (toggle) {
              restoringFocus = true;
              toggle.focus();
              restoringFocus = false;
            }
          }
        }
      }, 120);
    }

    function keepOpen() {
      window.clearTimeout(closeTimer);
    }

    items.forEach(function (item) {
      item.addEventListener('mouseenter', function () {
        activate(item, false);
      });
      item.addEventListener('mouseleave', function () {
        deactivate(false);
      });
      item.addEventListener('focusin', function () {
        if (restoringFocus) return;
        activate(item, false);
      });
    });

    Array.prototype.forEach.call(header.querySelectorAll('[data-mega-toggle]'), function (toggle) {
      toggle.addEventListener('click', function (event) {
        event.preventDefault();
        var item = toggle.closest('[data-mega-target]');
        if (item) activate(item, true);
      });
    });

    /* Plain dropdowns: items that have child links but no bento panel. Hover
       and focus open them; the dot button is the way in for keyboard and
       touch. Opening one closes the bento panel and any other dropdown. */
    var dropdownItems = Array.prototype.slice.call(header.querySelectorAll('[data-bento-dropdown]'));
    var dropdownTimer = null;

    function setDropdown(item, open) {
      item.classList.toggle('is-open', open);
      if (!open) delete item.dataset.clickOpen;
      var toggle = item.querySelector('[data-bento-dropdown-toggle]');
      if (toggle) toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    }

    function openDropdown(item) {
      window.clearTimeout(dropdownTimer);
      dropdownItems.forEach(function (other) {
        if (other !== item) setDropdown(other, false);
      });
      if (activeItem) deactivate(false);
      flipFlyouts(item);
      setDropdown(item, true);
    }

    /* A third-level card opens to the right of the dropdown; an item near the
       right edge of the screen would push it off-screen, so it opens to the
       left instead. Measured unflipped on every open: the page width can have
       changed since the last one. Hidden cards are still laid out, so the
       reading is valid before they show. */
    function flipFlyouts(item) {
      var edge = document.documentElement.clientWidth;
      Array.prototype.forEach.call(item.querySelectorAll('.m-header-bento__dropdown-sublist'), function (flyout) {
        flyout.classList.remove('flipped');
        flyout.classList.toggle('flipped', flyout.getBoundingClientRect().right > edge);
      });
    }

    function closeDropdowns(returnFocusTo) {
      dropdownItems.forEach(function (item) {
        if (item.classList.contains('is-open') && returnFocusTo) {
          var toggle = item.querySelector('[data-bento-dropdown-toggle]');
          if (toggle) toggle.focus();
        }
        setDropdown(item, false);
      });
    }

    dropdownItems.forEach(function (item) {
      item.addEventListener('mouseenter', function () {
        openDropdown(item);
      });
      item.addEventListener('mouseleave', function () {
        dropdownTimer = window.setTimeout(function () {
          setDropdown(item, false);
        }, 120);
      });
      item.addEventListener('focusin', function () {
        openDropdown(item);
      });
      item.addEventListener('focusout', function (event) {
        if (!item.contains(event.relatedTarget)) setDropdown(item, false);
      });
      var toggle = item.querySelector('[data-bento-dropdown-toggle]');
      if (toggle) {
        /* By the time the button is clicked, hover (mouse, or the emulated
           mouseenter of a tap) or focus (Tab) has usually opened the dropdown
           already, so "open means close" would shut it on the first click and
           a tap could never open it. The first click therefore always opens
           and moves into the list; only a dropdown this button opened closes
           on the next click. */
        toggle.addEventListener('click', function (event) {
          event.preventDefault();
          if (item.classList.contains('is-open') && item.dataset.clickOpen === 'true') {
            setDropdown(item, false);
            return;
          }
          openDropdown(item);
          item.dataset.clickOpen = 'true';
          var first = item.querySelector('.m-header-bento__dropdown a[href]');
          if (first) first.focus();
        });
      }
    });

    header.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') closeDropdowns(true);
    });

    panels.addEventListener('mouseenter', keepOpen);
    panels.addEventListener('mouseleave', function () {
      deactivate(false);
    });

    if (overlay) {
      overlay.addEventListener('click', function () {
        deactivate(false);
        // The overlay also dims the page under a plain dropdown (a tap opens
        // one without any mouseleave to close it).
        closeDropdowns(false);
      });
    }

    header.addEventListener('focusout', function (event) {
      if (!header.contains(event.relatedTarget)) deactivate(false);
    });

    header.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') deactivate(true);
    });

    /* Theme editor: selecting a panel block (or a block inside one) in the
       sidebar opens that panel, so it can be edited without hovering the menu
       item; deselecting closes it. Desktop only — below the tablet breakpoint
       the panels live in the drawer layers. A panel whose `menu_name` matches
       no menu item has no item to open it, and stays closed. */
    var editorQuery = window.matchMedia('(max-width: 1023px)');
    function itemForBlock(event) {
      if (editorQuery.matches || !header.contains(event.target)) return null;
      var panel = event.target.closest('[data-mega-panel]') || event.target.querySelector('[data-mega-panel]');
      if (!panel) return null;
      return header.querySelector('[data-mega-target="' + panel.getAttribute('data-mega-panel') + '"]');
    }
    function onBlockSelect(event) {
      var item = itemForBlock(event);
      if (item) activate(item, false);
    }
    function onBlockDeselect(event) {
      if (itemForBlock(event)) deactivate(false);
    }
    document.addEventListener('shopify:block:select', onBlockSelect);
    document.addEventListener('shopify:block:deselect', onBlockDeselect);
    disposers.push(function () {
      document.removeEventListener('shopify:block:select', onBlockSelect);
      document.removeEventListener('shopify:block:deselect', onBlockDeselect);
    });

    /* Mobile: the drawer leaves an empty slot per top-level item. When a submenu
       opens, the matching panel is moved into that slot and marked active; when
       it closes the panel goes back to the panel container. Moving (not cloning)
       keeps one instance, so ids and JS hooks stay unique. */
    function syncDrawer() {
      submenus.forEach(function (submenu) {
        var slot = submenu.querySelector('[data-mega-slot]');
        if (!slot) return;

        var target = slot.getAttribute('data-mega-slot');
        var panel = panelFor(target);
        if (!panel) return;

        if (submenu.classList.contains('open')) {
          if (panel.parentNode !== slot) {
            slot.appendChild(panel);
            panel.classList.add('is-active');
          }
        } else if (panel.parentNode === slot) {
          sendHome(panel);
          panel.classList.remove('is-active');
        }
      });
    }

    var observer = new MutationObserver(syncDrawer);
    submenus.forEach(function (submenu) {
      observer.observe(submenu, { attributes: true, attributeFilter: ['class'] });
    });
    disposers.push(function () {
      observer.disconnect();
    });

    /* A panel left in a drawer slot when the viewport grows past the tablet
       breakpoint would make every desktop hover a no-op until a reload: the
       drawer is hidden, so its `open` class is never removed and nothing moves
       the panel home. Bring everything back on a breakpoint change. */
    function resetForBreakpoint() {
      submenus.forEach(function (submenu) {
        submenu.classList.remove('open');
      });
      Array.prototype.forEach.call(header.querySelectorAll('[data-mega-panel]'), function (panel) {
        sendHome(panel);
        panel.classList.remove('is-active');
      });
      panels.classList.remove('is-open');
      header.classList.remove('m-bento-active');
      if (activeItem) {
        activeItem.classList.remove('is-mega-open');
        setExpanded(activeItem, false);
        activeItem = null;
      }
      syncDrawer();

      /* A mobile menu left open across the breakpoint is invisible on desktop
         (the mobile bar is hidden) but keeps `prevent-scroll` on the page, so
         the desktop could not scroll and nothing would close it. */
      if (drawer && drawer.isOpen()) drawer.close();
    }

    document.addEventListener('matchTablet', resetForBreakpoint);
    document.addEventListener('unmatchTablet', resetForBreakpoint);
    disposers.push(function () {
      document.removeEventListener('matchTablet', resetForBreakpoint);
      document.removeEventListener('unmatchTablet', resetForBreakpoint);
    });

    syncDrawer();
  }

  /* Sticky bar. Replaces the vendor `header.js` with the same contract:
     `stuck` / `header-sticky-always` (per the section's `data-sticky`),
     `scroll-up` / `scroll-down` and the body classes other components read
     (`assets/main.css`).

     The budget is the vendor's: 20px plus every header-group section plus the
     topbar. The vendor measured the group wrapper, which carries the height of
     everything in it — kept as is so the thresholds do not move. */
  function initSticky(header, disposers) {
    var wrapper = header.querySelector('.m-header__wrapper');
    if (!wrapper) return;

    var mode = header.dataset.sticky;
    var SCROLL_SLOP = 6;
    var lastScroll = 0;
    var extraSpace = 0;
    var headerHeight = 0;

    function measure() {
      extraSpace = 20;
      Array.prototype.forEach.call(document.querySelectorAll('.shopify-section-group-header-group'), function (group) {
        if (!group.classList.contains('m-section-header') && !group.classList.contains('m-section-scaling-logo')) {
          extraSpace += group.offsetHeight;
        }
      });
      var topbar = header.querySelector('.m-topbar');
      if (topbar) extraSpace += topbar.offsetHeight;
      headerHeight = wrapper.offsetHeight;
    }

    function update() {
      var y = window.scrollY;
      if (y <= extraSpace) {
        header.classList.remove('scroll-up', 'stuck', 'header-sticky-always');
        document.body.classList.remove('header-scroll-up', 'header-scroll-down');
        return;
      }

      if (mode === 'on_scroll_up') {
        header.classList.add('stuck');
      } else if (mode === 'always') {
        header.classList.add('header-sticky-always');
      }

      // Direction changes count only past a few pixels. Firefox reports
      // fractional, slightly back-and-forth positions on a slow wheel or
      // touchpad scroll; each tiny reversal flipped scroll-down / scroll-up
      // and the bar started sliding and turned back, a few pixels up and
      // down (owner, 2026-10-14). lastScroll stays put until the move counts.
      if (Math.abs(y - lastScroll) < SCROLL_SLOP) return;

      if (y > headerHeight + extraSpace && y > lastScroll && !header.classList.contains('scroll-down')) {
        header.classList.remove('scroll-up');
        document.body.classList.remove('header-scroll-up');
        header.classList.add('scroll-down');
        document.body.classList.add('header-scroll-down');
      } else if (y < lastScroll && header.classList.contains('scroll-down')) {
        header.classList.remove('scroll-down');
        document.body.classList.remove('header-scroll-down');
        header.classList.add('scroll-up');
        document.body.classList.add('header-scroll-up');
      }
      lastScroll = y;
    }

    measure();
    // A reload part-way down the page gets the right state at once (the vendor
    // script waited for the first scroll event).
    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', measure);
    // The bar changes height at the tablet breakpoint too (desktop 72px vs
    // mobile 52px), so re-measure there as well as at the mobile one.
    function onMatchTablet() {
      measure();
      update();
    }
    function onUnmatchTablet() {
      measure();
      update();
    }
    function onMatchMobile() {
      measure();
      update();
    }
    function onUnmatchMobile() {
      measure();
      update();
    }
    document.addEventListener('matchTablet', onMatchTablet);
    document.addEventListener('unmatchTablet', onUnmatchTablet);
    document.addEventListener('matchMobile', onMatchMobile);
    document.addEventListener('unmatchMobile', onUnmatchMobile);

    // Section reload only guards against a double `boot()`, so without this
    // these window/document listeners would keep running for the header that
    // just got replaced.
    disposers.push(function () {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', measure);
      document.removeEventListener('matchTablet', onMatchTablet);
      document.removeEventListener('unmatchTablet', onUnmatchTablet);
      document.removeEventListener('matchMobile', onMatchMobile);
      document.removeEventListener('unmatchMobile', onUnmatchMobile);
    });
  }

  /* Mobile drawer. Replaces the vendor `mega-menu.js` for this header: the
     burger opens and closes it, the backdrop closes it, the dot buttons open
     the level-1/2 layers and the back buttons close them. Class names are the
     vendor's, because the markup, the "Base" block of `header-bento.css` and the panel mover
     above all read them.

     The drawer is a modal dialog (APG Dialog Modal; the role, `aria-modal`
     and name are in header-bento-drawer.liquid): opening the drawer or a
     submenu layer moves focus in (first focusable, or the layer's own back
     button); closing it — burger, backdrop, back button, the screen-reader
     close button or Escape — returns focus to whatever opened it.
     `openLayers` is a stack rather than one slot because level 2 nests
     inside level 1: Escape always unwinds the topmost layer first, same as
     the back button would. The one exception is the cart/search handover
     near the bottom: that focus is already managed elsewhere, so closing the
     drawer for it leaves focus alone instead of yanking it back to the
     burger.

     Tab is trapped in the burger, the drawer's round close button and
     whatever is on screen in the menu card: the burger (it keeps its icon)
     lives in the bar, outside the dialog element, but closes it as well. The
     rest of the bar stays reachable by pointer
     only, as before, so the cart/menu and search/menu switches keep working;
     nothing else is made `inert` — the bar shares a `<header>` with the
     drawer, and the body-level drawers it opens must be able to take focus
     the moment the menu closes. The trap is gated on `open`, so it is gone
     before either of them does.

     Closed submenu layers ARE made `inert`. The vendor keeps them visible
     whenever the drawer is open (only slid off the card and clipped), so
     Tab and a screen reader's virtual cursor would otherwise walk every
     hidden submenu. */
  function initDrawer(header, disposers) {
    var drawer = header.querySelector('#m-menu-drawer');
    if (!drawer) return null;

    var backdrop = drawer.querySelector('.m-menu-drawer__backdrop');
    var content = drawer.querySelector('.m-menu-drawer__content');
    var menu = drawer.querySelector('.m-menu-mobile');
    var mobileBar = header.querySelector('.m-header__mobile');
    var burger = header.querySelector('.m-hamburger-box');
    var closeButton = drawer.querySelector('[data-menu-drawer-close]');
    var open = false;
    var openLayers = [];

    function setHeight() {
      if (!mobileBar) return;
      drawer.style.setProperty(
        '--menu-drawer-height',
        Math.round(window.innerHeight - mobileBar.getBoundingClientRect().bottom) + 'px'
      );
    }

    function firstFocusable(scope) {
      return scope ? scope.querySelector('a[href], button:not([disabled]):not([tabindex="-1"])') : null;
    }

    /* Same list as `assets/description-drawer.js`: the drawer footer hosts
       the locale switchers, and a bento panel moved into a slot can bring
       product cards and a scrollable track (`tabindex="0"`). */
    var FOCUSABLE =
      'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
    /* The Tab trap also stops on <summary> (details disclosures) but never on
       a button a <summary> or tabindex -1 has taken out of the order. */
    var FOCUSABLE_TRAP = FOCUSABLE + ', details > summary:first-of-type';

    /* A layer's state lives in three places that must not drift: the vendor
       `open` class (CSS and the panel mover read it), `inert` while closed
       (see above) and `aria-expanded` on its toggles — the dot button and,
       for a `#` placeholder link, the link. Both are direct children of the
       layer's item; a deeper query would also catch level-2 toggles. */
    function setLayer(layer, isOpen) {
      layer.classList.toggle('open', isOpen);
      layer.toggleAttribute('inert', !isOpen);
      Array.prototype.forEach.call(layer.parentNode.children, function (child) {
        if (child.matches('[data-toggle-submenu]') && child.hasAttribute('aria-expanded')) {
          child.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
        }
      });
    }

    Array.prototype.forEach.call(drawer.querySelectorAll('.m-megamenu-mobile'), function (layer) {
      setLayer(layer, layer.classList.contains('open'));
    });

    /* What Tab may land on: the burger, then the topmost open layer — the
       layers below it are covered by it — or, with none open, the first
       level. Hidden and inert elements drop out; `visibility` is checked
       because the drawer keeps it on a delay while it closes. */
    function trapList() {
      var scope = openLayers.length ? openLayers[openLayers.length - 1].layer : content;
      var list = [];
      if (scope) collectFocusable(scope, list);
      if (closeButton && canFocus(closeButton)) list.unshift(closeButton);
      if (burger) list.unshift(burger);
      return list;
    }

    /* Content of a closed <details> (the country / language selectors) stays
       laid out but cannot take focus; only its <summary> can. */
    function inClosedDetails(el) {
      for (var d = el.closest('details'); d; d = d.parentElement && d.parentElement.closest('details')) {
        if (!d.open && !(el.tagName === 'SUMMARY' && el.parentElement === d)) return true;
      }
      return false;
    }

    function isShown(el) {
      return el.offsetParent !== null && !el.closest('[inert]') && !inClosedDetails(el);
    }

    function canFocus(el) {
      return (
        el.getAttribute('tabindex') !== '-1' &&
        el.offsetParent !== null &&
        !el.closest('[inert]') &&
        !inClosedDetails(el) &&
        window.getComputedStyle(el).visibility !== 'hidden'
      );
    }

    /* Document order, descending into open shadow roots: the "Log in" button
       lives inside Shopify's <shopify-account>, whose host is not focusable
       itself (tabindex -1). */
    function collectFocusable(root, list) {
      Array.prototype.forEach.call(root.children, function (el) {
        if (el.matches(FOCUSABLE_TRAP) && canFocus(el)) list.push(el);
        if (el.shadowRoot && isShown(el)) collectFocusable(el.shadowRoot, list);
        collectFocusable(el, list);
      });
    }

    /* Focus inside a shadow root reports the host as document.activeElement. */
    function deepActiveElement() {
      var active = document.activeElement;
      while (active && active.shadowRoot && active.shadowRoot.activeElement) active = active.shadowRoot.activeElement;
      return active;
    }

    /* Every Tab is placed by hand rather than only wrapping at the ends:
       DOM order would send Tab from the burger into the first level even
       while a submenu covers it, and Shift+Tab from the burger into the
       cart button. Focus outside the list (a tap on bare drawer space)
       re-enters at the matching end. */
    function trapTab(event) {
      var list = trapList();
      if (!list.length) return;
      event.preventDefault();
      var index = list.indexOf(deepActiveElement());
      var next;
      if (index === -1) next = event.shiftKey ? list[list.length - 1] : list[0];
      else next = list[(index + (event.shiftKey ? list.length - 1 : 1)) % list.length];
      next.focus();
    }

    function openMenu() {
      setHeight();
      document.documentElement.classList.add('prevent-scroll');
      drawer.classList.add('open');
      if (mobileBar) mobileBar.classList.add('header-drawer-open');
      open = true;
      var target = firstFocusable(content);
      if (target) target.focus({ preventScroll: true });
    }

    // `returnFocus` defaults to true; pass false when something else (the
    // cart or search popup) is already taking focus where it needs to go.
    function closeMenu(returnFocus) {
      Array.prototype.forEach.call(drawer.querySelectorAll('.m-megamenu-mobile'), function (layer) {
        setLayer(layer, false);
      });
      if (menu) menu.classList.remove('m-submenu-open', 'm-submenu-open--level-1', 'm-submenu-open--level-2');
      drawer.classList.remove('open');
      document.documentElement.classList.remove('prevent-scroll');
      if (mobileBar) mobileBar.classList.remove('header-drawer-open');
      if (burger) burger.classList.remove('active');
      open = false;
      // Every layer just got force-closed above regardless of the stack.
      openLayers.length = 0;
      if (returnFocus !== false && burger) burger.focus();
    }

    function openSubMenu(layer, level, opener) {
      if (content) content.classList.add('open-submenu');
      if (menu) {
        menu.classList.add('m-submenu-open');
        menu.classList.add('m-submenu-open--level-' + level);
      }
      setLayer(layer, true);
      openLayers.push({ layer: layer, level: level, opener: opener });
      /* `preventScroll`: the layer is still off to the right (it slides in
         with a transform), and a plain focus() makes the browser scroll the
         clipping wrapper sideways to reveal it — `overflow: hidden` still
         scrolls programmatically. The wrapper then showed the last closed
         layer where the opened one should be (a "double menu"). */
      var target = layer.querySelector('.m-menu-mobile__back-button') || firstFocusable(layer);
      if (target) target.focus({ preventScroll: true });
    }

    function closeSubMenu(layer, level) {
      if (level === '1' && menu) menu.classList.remove('m-submenu-open');
      if (menu) menu.classList.remove('m-submenu-open--level-' + level);
      setLayer(layer, false);
      if (content) content.classList.remove('open-submenu');

      for (var i = openLayers.length - 1; i >= 0; i--) {
        if (openLayers[i].layer === layer) {
          var closed = openLayers.splice(i, 1)[0];
          if (closed.opener) closed.opener.focus({ preventScroll: true });
          break;
        }
      }
    }

    if (burger) {
      burger.addEventListener('click', function () {
        if (open) closeMenu();
        else openMenu();
        // `header-bento.js` above mirrors this class into `aria-expanded`.
        burger.classList.toggle('active', open);
      });
    }

    if (backdrop) {
      backdrop.addEventListener('click', function () {
        closeMenu();
      });
    }

    if (closeButton) {
      closeButton.addEventListener('click', function () {
        closeMenu();
      });
    }

    /* One delegated listener per level: the dot button and a placeholder link
       both carry `data-toggle-submenu="<level>"`, and the layer is a sibling
       inside the same item. */
    drawer.addEventListener('click', function (event) {
      var toggle = event.target.closest('[data-toggle-submenu]');
      if (!toggle || toggle.classList.contains('m-menu-mobile__back-button')) return;
      var item = toggle.closest('.m-menu-mobile__item');
      var layer = item && item.querySelector('.m-megamenu-mobile');
      if (!layer) return;
      event.preventDefault();
      openSubMenu(layer, toggle.dataset.toggleSubmenu, toggle);
    });

    drawer.addEventListener('click', function (event) {
      var back = event.target.closest('.m-menu-mobile__back-button');
      if (!back) return;
      var item = back.closest('.m-menu-mobile__item');
      var layer = item && item.querySelector('.m-megamenu-mobile');
      if (!layer) return;
      event.preventDefault();
      closeSubMenu(layer, back.dataset.level);
    });

    /* Escape unwinds one step at a time, same as the back button: the
       topmost submenu layer first (if any), the whole drawer only once no
       layer is open. Bound on `document`, not `drawer`, because focus can sit
       on the burger (just before a layer has opened) as well as inside the
       drawer. Tab is trapped from the same listener (see the note above
       `initDrawer`). */
    function onKeydown(event) {
      if (!open) return;
      if (event.key === 'Tab') {
        trapTab(event);
        return;
      }
      if (event.key !== 'Escape') return;
      if (openLayers.length) {
        var top = openLayers[openLayers.length - 1];
        closeSubMenu(top.layer, top.level);
      } else {
        closeMenu();
      }
    }
    document.addEventListener('keydown', onKeydown);

    document.addEventListener('matchMobile', setHeight);
    document.addEventListener('unmatchMobile', setHeight);

    /* Opening the cart or the search popup closes the menu, like the vendor
       script did. Both are emitted on the shared `LeonartoEvents` bus. Focus
       is left alone here (`closeMenu(false)`): the cart/search popup is about
       to place it themselves, and pulling it back to the burger first would
       just bounce it. */
    var unsubscribeCart, unsubscribeSearch;
    if (window.LeonartoEvents && window.LeonartoTheme && window.LeonartoTheme.pubSubEvents) {
      var events = window.LeonartoTheme.pubSubEvents;
      unsubscribeCart = window.LeonartoEvents.subscribe(events.openCartDrawer, function () {
        if (open) closeMenu(false);
      });
      unsubscribeSearch = window.LeonartoEvents.subscribe(events.openSearchPopup, function () {
        if (open) closeMenu(false);
      });
    }

    // The document-level Escape/breakpoint listeners and the LeonartoEvents
    // subscriptions all outlive `drawer` on a section reload unless removed
    // by hand.
    disposers.push(function () {
      document.removeEventListener('keydown', onKeydown);
      document.removeEventListener('matchMobile', setHeight);
      document.removeEventListener('unmatchMobile', setHeight);
      if (unsubscribeCart) unsubscribeCart();
      if (unsubscribeSearch) unsubscribeSearch();
    });

    return {
      isOpen: function () {
        return open;
      },
      close: closeMenu,
    };
  }

  /* Section instances keyed by `data-section-id`, so `shopify:section:unload`
     can find and tear down the exact header it is about to replace. The
     `megaInit` guard above only stops a double `boot()`; without this map,
     the old instance's window/document listeners and MutationObservers keep
     running against a detached header forever — e.g. the `prevent-scroll`
     observer in `initPanels`, left connected, re-adding the scroll lock from
     a `menuDrawer` that no longer exists on screen. */
  var instances = {};

  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll('m-header[data-header-bento]'), function (header) {
      // The editor re-renders the section, so the element itself is the guard:
      // a fresh element has no marker and gets initialised once.
      if (header.dataset.megaInit === 'true') return;
      header.dataset.megaInit = 'true';
      var disposers = [];
      initSticky(header, disposers);
      var drawer = initDrawer(header, disposers);
      initPanels(header, drawer, disposers);
      if (header.dataset.sectionId) instances[header.dataset.sectionId] = disposers;
    });
  }

  function teardown(event) {
    var sectionId = event.detail && event.detail.sectionId;
    var disposers = sectionId && instances[sectionId];
    if (!disposers) return;
    disposers.forEach(function (dispose) {
      dispose();
    });
    delete instances[sectionId];
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('shopify:section:unload', teardown);
})();
